import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  Maintenance,
  MaintenanceBilling,
  MaintenanceBillingOverview,
  MaintenanceMaterial
} from '../Models/maintenance.model';
import { ChecklistItem, ChecklistItemComment } from '../Models/checklist-item.model';
import { MaintenanceChecklist } from '../Models/maintenance-checklist.model';

export interface ChecklistNamingPayload {
  name?: string;
}

export interface MaintenanceProgressPayload {
  progressPercent: number;
  notes?: string;
}

export interface MaintenanceMaterialPayload {
  name: string;
  description?: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
  isPacked: boolean;
}

export interface MaintenanceReportEmailPayload {
  email: string;
}

export interface ServiceCallPhotoDto {
  id: number;
  fileName: string;
  url: string;
  uploadedAtUtc: string;
}

export interface ServiceCallReportPdfDto {
  id: number;
  fileName: string;
  url: string;
  createdAtUtc: string;
}

export interface ServiceCallReportUploadPayload {
  fileName?: string;
  pdfBase64: string;
}

export interface MaintenanceBillingUpdatePayload {
  billingStatus: string;
  billingNote?: string | null;
  lines: any[];
}

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private apiUrl = environment.apiBaseUrl + '/maintenance';
  private checklistUrl = environment.apiBaseUrl + '/maintenanceChecklist';
  private checklistItemUrl = environment.apiBaseUrl + '/checklistItem';
  private equipmentUrl = environment.apiBaseUrl + '/equipment';

  constructor(private http: HttpClient) { }

  getMaintenances(): Observable<Maintenance[]> {
    return this.http.get<Maintenance[]>(this.apiUrl);
  }

  getUpcomingMaintenances(daysAhead: number = 7): Observable<Maintenance[]> {
    const params = new HttpParams().set('daysAhead', daysAhead);
    return this.http.get<Maintenance[]>(`${this.apiUrl}/upcoming`, { params });
  }

  getMaintenancesByEquipment(equipmentId: number): Observable<Maintenance[]> {
    return this.http.get<Maintenance[]>(`${this.apiUrl}/ByEquipment/${equipmentId}`);
  }

  getMaintenance(id: number): Observable<Maintenance> {
    return this.http.get<Maintenance>(`${this.apiUrl}/${id}`);
  }

  getBillingOverview(status: 'open' | 'billed' | 'all' = 'open'): Observable<MaintenanceBillingOverview[]> {
    const params = new HttpParams().set('status', status);
    return this.http.get<MaintenanceBillingOverview[]>(`${this.apiUrl}/billing`, { params });
  }

  getBilling(id: number): Observable<MaintenanceBilling> {
    return this.http.get<MaintenanceBilling>(`${this.apiUrl}/${id}/billing`);
  }

  updateBilling(id: number, payload: MaintenanceBillingUpdatePayload): Observable<MaintenanceBilling> {
    return this.http.put<MaintenanceBilling>(`${this.apiUrl}/${id}/billing`, payload);
  }

  createMaintenance(maintenance: Partial<Maintenance>): Observable<Maintenance> {
    return this.http.post<Maintenance>(this.apiUrl, maintenance);
  }

  updateMaintenance(id: number, maintenance: Partial<Maintenance>): Observable<Maintenance> {
    return this.http.put<Maintenance>(`${this.apiUrl}/${id}`, maintenance);
  }

  deleteMaintenance(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  sendReportEmail(id: number, payload: MaintenanceReportEmailPayload): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/send-report-email`, payload);
  }

  getServiceCallPhotos(id: number): Observable<ServiceCallPhotoDto[]> {
    return this.http.get<ServiceCallPhotoDto[]>(`${this.apiUrl}/${id}/photos`);
  }

  uploadServiceCallPhoto(id: number, file: Blob, fileName: string): Observable<ServiceCallPhotoDto> {
    const formData = new FormData();
    formData.append('file', file, fileName);
    return this.http.post<ServiceCallPhotoDto>(`${this.apiUrl}/${id}/photos`, formData);
  }

  deleteServiceCallPhoto(id: number, photoId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/photos/${photoId}`);
  }

  getLatestServiceCallReportPdf(id: number): Observable<ServiceCallReportPdfDto> {
    return this.http.get<ServiceCallReportPdfDto>(`${this.apiUrl}/${id}/report-pdfs/latest`);
  }

  /** Alle gespeicherten Report-Versionen (neueste zuerst). */
  getServiceCallReportPdfs(id: number): Observable<ServiceCallReportPdfDto[]> {
    return this.http.get<ServiceCallReportPdfDto[]>(`${this.apiUrl}/${id}/report-pdfs`);
  }

  uploadServiceCallReportPdf(id: number, payload: ServiceCallReportUploadPayload): Observable<ServiceCallReportPdfDto> {
    return this.http.post<ServiceCallReportPdfDto>(`${this.apiUrl}/${id}/report-pdfs`, payload);
  }

  addProgressUpdate(id: number, payload: MaintenanceProgressPayload): Observable<Maintenance> {
    return this.http.post<Maintenance>(`${this.apiUrl}/${id}/progress-updates`, payload);
  }

  addMaterial(maintenanceId: number, payload: MaintenanceMaterialPayload): Observable<MaintenanceMaterial> {
    return this.http.post<MaintenanceMaterial>(`${this.apiUrl}/${maintenanceId}/materials`, payload);
  }

  updateMaterial(maintenanceId: number, materialId: number, payload: MaintenanceMaterialPayload): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${maintenanceId}/materials/${materialId}`, payload);
  }

  deleteMaterial(maintenanceId: number, materialId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${maintenanceId}/materials/${materialId}`);
  }

  createChecklist(checklist: MaintenanceChecklist): Observable<MaintenanceChecklist> {
    return this.http.post<MaintenanceChecklist>(this.checklistUrl, checklist);
  }

  getChecklist(id: number): Observable<MaintenanceChecklist> {
    return this.http.get<MaintenanceChecklist>(`${this.checklistUrl}/${id}`);
  }

  updateChecklist(id: number, checklist: MaintenanceChecklist): Observable<void> {
    return this.http.put<void>(`${this.checklistUrl}/${id}`, checklist);
  }

  deleteChecklist(id: number): Observable<void> {
    return this.http.delete<void>(`${this.checklistUrl}/${id}`);
  }

  createChecklistItem(item: ChecklistItem): Observable<ChecklistItem> {
    return this.http.post<ChecklistItem>(this.checklistItemUrl, item);
  }

  getChecklistItem(id: number): Observable<ChecklistItem> {
    return this.http.get<ChecklistItem>(`${this.checklistItemUrl}/${id}`);
  }

  updateChecklistItem(id: number, item: ChecklistItem): Observable<void> {
    return this.http.put<void>(`${this.checklistItemUrl}/${id}`, item);
  }

  deleteChecklistItem(id: number): Observable<void> {
    return this.http.delete<void>(`${this.checklistItemUrl}/${id}`);
  }

  addChecklistItemComment(itemId: number, text: string): Observable<ChecklistItemComment> {
    return this.http.post<ChecklistItemComment>(`${this.checklistItemUrl}/${itemId}/comments`, { text });
  }

  deleteChecklistItemComment(itemId: number, commentId: number): Observable<void> {
    return this.http.delete<void>(`${this.checklistItemUrl}/${itemId}/comments/${commentId}`);
  }

  getChecklistTemplates(): Observable<MaintenanceChecklist[]> {
    return this.http.get<MaintenanceChecklist[]>(`${this.checklistUrl}/templates`);
  }

  createChecklistTemplate(checklist: MaintenanceChecklist): Observable<MaintenanceChecklist> {
    return this.http.post<MaintenanceChecklist>(`${this.checklistUrl}/template`, checklist);
  }

  assignTemplateToMaintenance(checklistId: number, maintenanceId: number, payload?: ChecklistNamingPayload): Observable<MaintenanceChecklist> {
    return this.http.post<MaintenanceChecklist>(
      `${this.checklistUrl}/assignToMaintenance/${checklistId}/${maintenanceId}`,
      payload ?? {}
    );
  }

  convertChecklistToTemplate(checklistId: number, payload?: ChecklistNamingPayload): Observable<MaintenanceChecklist> {
    return this.http.post<MaintenanceChecklist>(`${this.checklistUrl}/${checklistId}/convertToTemplate`, payload ?? {});
  }
}
